import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Package, GitMerge, CheckCircle2, Clock, AlertTriangle, XCircle,
  Play, FileCode, Shield, ChevronRight, ChevronDown, Eye, Code,
  ArrowRight, RefreshCw, Terminal, Layers, Users, Calendar,
  FileText, Server, Workflow, TestTube, Lock, Download, ExternalLink
} from 'lucide-react';
import { pullRequests, pipelineStages, guardrails } from '../data/pullRequests';
import { changeSpecs } from '../data/changeSpecs';
import {
  assetChangeSpecs,
  connectorCodegenArtifacts,
  workflowTemplates,
  policyControls,
  testArtifacts,
  evidenceBundleTemplate,
  assetAuditEvents
} from '../data/assetAutomationData';
import { serviceChangeSpecs, serviceCompiledDiffs } from '../data/serviceAutomationData';
import { useToast } from '../context/ToastContext';

const statusColors = {
  open: { bg: 'bg-primary/10', text: 'text-primary', label: 'Open' },
  merged: { bg: 'bg-slate-200', text: 'text-slate-700', label: 'Merged' },
  closed: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Closed' },
  draft: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Draft' }
};

const checkStatusIcons = {
  passed: { icon: CheckCircle2, color: 'text-primary' },
  failed: { icon: XCircle, color: 'text-slate-900' },
  running: { icon: RefreshCw, color: 'text-primary animate-spin' },
  pending: { icon: Clock, color: 'text-gray-400' }
};

const stageStatusColors = {
  completed: 'bg-primary',
  running: 'bg-primary animate-pulse',
  pending: 'bg-gray-300',
  failed: 'bg-slate-900'
};

// Merge all change specs for combined view
const getAllChangeSpecs = () => {
  const assetSpecs = Object.values(assetChangeSpecs);
  const serviceSpecs = Object.values(serviceChangeSpecs);
  return [...changeSpecs, ...assetSpecs, ...serviceSpecs];
};

export const Changes = () => {
  const location = useLocation();
  const { addToast } = useToast();
  const [selectedPR, setSelectedPR] = useState(null);
  const [activeDiffTab, setActiveDiffTab] = useState('workflow');
  const [showGuardrails, setShowGuardrails] = useState(false);
  const [dryRunEnabled, setDryRunEnabled] = useState(true);

  // New PR creation state
  const [showProposeBundle, setShowProposeBundle] = useState(false);
  const [newBundleSpec, setNewBundleSpec] = useState(null);
  const [bundleCreationStep, setBundleCreationStep] = useState(1);
  const [pipelineSimulation, setPipelineSimulation] = useState(null);
  const [compiledDiffs, setCompiledDiffs] = useState(null);

  // Handle navigation state from Assets or Services page
  useEffect(() => {
    if (location.state?.proposeBundle && location.state?.changeSpec) {
      setNewBundleSpec(location.state.changeSpec);
      setCompiledDiffs(location.state.compiledDiffs || null);
      setShowProposeBundle(true);
      setBundleCreationStep(1);
    }
  }, [location.state]);

  const handleAction = (action) => {
    addToast(`Demo action: ${action}`, 'info');
  };

  const getChangeSpec = (id) => {
    const allSpecs = getAllChangeSpecs();
    return allSpecs.find(cs => cs.id === id);
  };


  const simulatePipeline = () => {
    setPipelineSimulation({
      status: 'running',
      stages: [
        { name: 'Schema Validation', status: 'completed', output: 'Change spec validates against schema v1.0' },
        { name: 'Plan', status: 'completed', output: 'Generated execution plan - 3 resources to update' },
        { name: 'Tests', status: 'completed', output: '14/14 tests passed - unit, contract, simulation' },
        { name: 'Policy', status: 'completed', output: 'Policy simulation passed - 1 violation remediated' },
        { name: 'Approval', status: 'running', output: 'Awaiting 3 approvals...' },
        { name: 'Apply', status: 'pending', output: '' },
        { name: 'Verify', status: 'pending', output: '' },
        { name: 'Evidence', status: 'pending', output: '' }
      ]
    });

    // Simulate pipeline progress
    setTimeout(() => {
      setPipelineSimulation(prev => ({
        ...prev,
        stages: prev.stages.map(s =>
          s.name === 'Approval' ? { ...s, status: 'completed', output: '3/3 approvals received' } :
            s.name === 'Apply' ? { ...s, status: 'running', output: 'Applying changes...' } : s
        )
      }));
    }, 2000);

    setTimeout(() => {
      setPipelineSimulation(prev => ({
        ...prev,
        status: 'completed',
        stages: prev.stages.map(s =>
          s.name === 'Apply' ? { ...s, status: 'completed', output: newBundleSpec?.type?.includes('service') || newBundleSpec?.type?.includes('drift') || newBundleSpec?.type?.includes('runbook') ? 'Configuration updated successfully' : '3 assets updated - status: refresh_scheduled' } :
            s.name === 'Verify' ? { ...s, status: 'completed', output: 'All health checks passed' } :
              s.name === 'Evidence' ? { ...s, status: 'completed', output: 'Evidence bundle EB-2026-0150 created' } : s
        )
      }));
      addToast('Pipeline completed - Evidence bundle generated', 'success');
    }, 4000);
  };

  // Determine which diffs to show based on the change spec type
  const getDiffTabs = () => {
    const specType = newBundleSpec?.type || '';
    if (specType.includes('service_spec') || specType.includes('runbook') || specType.includes('baseline') || specType.includes('hardening')) {
      return ['Terraform', 'Helm', 'Network Policy', 'Workflow', 'Tests'];
    }
    if (specType.includes('drift') || specType.includes('security') || specType.includes('revert')) {
      return ['Security Group', 'Workflow', 'Policy', 'Tests', 'Evidence'];
    }
    return ['Connector Actions', 'Workflow', 'Policy', 'Tests', 'Evidence'];
  };

  const getDiffContent = (tab) => {
    const diffs = compiledDiffs || serviceCompiledDiffs;
    const tabKey = tab.toLowerCase().replace(' ', '-');

    switch (tabKey) {
      case 'terraform':
        return diffs?.terraform || serviceCompiledDiffs.terraform;
      case 'helm':
        return diffs?.helm || serviceCompiledDiffs.helm;
      case 'network-policy':
        return diffs?.networkPolicy || serviceCompiledDiffs.networkPolicy;
      case 'security-group':
        return diffs?.securityGroupRevert || serviceCompiledDiffs.securityGroupRevert;
      case 'workflow':
        return diffs?.runbookWorkflow || serviceCompiledDiffs.runbookWorkflow || workflowTemplates.hardwareRefresh;
      case 'connector-actions':
        return connectorCodegenArtifacts.apiClientStub;
      case 'policy':
        return policyControls.opaRules;
      case 'tests':
        return testArtifacts.unitTests;
      case 'evidence':
        return JSON.stringify(evidenceBundleTemplate, null, 2);
      default:
        return 'No content available';
    }
  };

  const renderThreeLayerArtifacts = () => {
    const tabs = getDiffTabs();
    return (
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex border-b border-gray-200 bg-gray-50 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveDiffTab(tab.toLowerCase().replace(' ', '-'))}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${activeDiffTab === tab.toLowerCase().replace(' ', '-')
                  ? 'bg-white text-[#4a70ff] border-b-2 border-[#4a70ff]'
                  : 'text-gray-600 hover:text-black'
                }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="bg-gray-900 p-4 overflow-x-auto max-h-[400px] overflow-y-auto">
          <pre className="text-sm font-mono text-gray-100 whitespace-pre-wrap">
            {getDiffContent(tabs.find(t => t.toLowerCase().replace(' ', '-') === activeDiffTab) || tabs[0])}
          </pre>
        </div>
      </div>
    );
  };

  const renderProposeBundleFlow = () => {
    if (!newBundleSpec) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 w-full max-w-5xl max-h-[95vh] overflow-hidden my-auto">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-[#4a70ff]" />
              <div>
                <h2 className="text-lg font-semibold">Create Action Bundle</h2>
                <p className="text-sm text-gray-500">{newBundleSpec.title}</p>
              </div>
            </div>
            <button onClick={() => { setShowProposeBundle(false); setNewBundleSpec(null); setBundleCreationStep(1); setPipelineSimulation(null); }} className="text-gray-400 hover:text-gray-600">
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          {/* Step Progress */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center justify-between">
              {['Intent', 'Change Spec', 'Compiled Diffs', 'Bundle Details', 'Checks', 'Apply'].map((step, idx) => (
                <React.Fragment key={step}>
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${bundleCreationStep > idx + 1 ? 'bg-primary text-white' :
                        bundleCreationStep === idx + 1 ? 'bg-primary text-white' :
                          'bg-gray-200 text-gray-500'
                      }`}>
                      {bundleCreationStep > idx + 1 ? <CheckCircle2 className="w-4 h-4" /> : idx + 1}
                    </div>
                    <span className="text-xs text-gray-500 mt-1">{step}</span>
                  </div>
                  {idx < 5 && <div className={`flex-1 h-0.5 mx-2 ${bundleCreationStep > idx + 1 ? 'bg-primary' : 'bg-gray-200'}`} />}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(95vh-200px)]">
            {/* Step 1: Intent */}
            {bundleCreationStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Intent
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Title</label>
                      <p className="text-black">{newBundleSpec.title}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Type</label>
                      <span className="ml-2 px-2 py-0.5 bg-[#4a70ff]/10 text-[#4a70ff] rounded text-sm">{newBundleSpec.type}</span>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Targets</label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {newBundleSpec.targets?.map((t, i) => (
                          <span key={i} className="px-2 py-1 bg-gray-200 rounded text-sm">{t.name || t.id || t}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Risk Level</label>
                      <span className={`ml-2 px-2 py-0.5 rounded text-sm ${newBundleSpec.risk === 'high' ? 'bg-slate-900 text-white' :
                          newBundleSpec.risk === 'medium' ? 'bg-slate-200 text-slate-700' :
                            'bg-primary/10 text-primary'
                        }`}>{newBundleSpec.risk}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Required Approvals</h4>
                  <div className="flex flex-wrap gap-2">
                    {newBundleSpec.required_approvals?.map((approver, i) => (
                      <span key={i} className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-500" />
                        {approver}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-primary/10 border border-primary/20 rounded-lg">
                  <input
                    type="checkbox"
                    checked={dryRunEnabled}
                    onChange={() => setDryRunEnabled(!dryRunEnabled)}
                    className="rounded border-gray-300"
                    id="dryRun"
                  />
                  <label htmlFor="dryRun" className="text-sm text-primary">
                    <span className="font-medium">Dry run enabled</span> - Preview changes without applying
                  </label>
                </div>
              </div>
            )}

            {/* Step 2: Change Spec JSON */}
            {bundleCreationStep === 2 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Code className="w-4 h-4" /> Change Spec JSON (Read-only)
                  </h3>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    <span className="text-sm text-primary">Schema validated</span>
                  </div>
                </div>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto max-h-[400px] overflow-y-auto">
                  <pre className="text-sm text-gray-100 font-mono">
                    {JSON.stringify(newBundleSpec, null, 2)}
                  </pre>
                </div>
                <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary" />
                  <span className="text-sm text-primary">Change spec validates against schema v1.0 - No freeform scripts detected</span>
                </div>
              </div>
            )}

            {/* Step 3: Compiled Diffs - Three Layer Artifacts */}
            {bundleCreationStep === 3 && (
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Layers className="w-4 h-4" /> Compiled Diffs - Three Layer Artifacts
                </h3>
                <p className="text-sm text-gray-500">
                  Generated artifacts for Connectors, Workflows, and Guardrails layers
                </p>
                {renderThreeLayerArtifacts()}
              </div>
            )}

            {/* Step 4: Bundle Details */}
            {bundleCreationStep === 4 && (
              <div className="space-y-6">
                <h3 className="font-semibold flex items-center gap-2">
                  <Package className="w-4 h-4" /> Bundle Details
                </h3>

                <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Package className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-primary">{newBundleSpec.id}</span>
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-medium">Draft</span>
                      </div>
                      <p className="text-sm text-gray-500">{newBundleSpec.title}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                    <div>
                      <label className="text-xs text-gray-500">Author</label>
                      <p className="text-sm">Asset Intelligence</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Branch</label>
                      <p className="text-sm font-mono text-xs">asset/{newBundleSpec.type}/{newBundleSpec.id.toLowerCase()}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Target</label>
                      <p className="text-sm font-mono text-xs">main</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Labels</label>
                      <div className="flex gap-1">
                        <span className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">{newBundleSpec.type}</span>
                        <span className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">{newBundleSpec.risk}-risk</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Policy Checks */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4" /> Policy Checks
                  </h4>
                  <div className="space-y-2">
                    {newBundleSpec.policy_checks?.map((check, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        {check.status === 'pass' ? (
                          <CheckCircle2 className="w-5 h-5 text-primary" />
                        ) : check.status === 'violation' ? (
                          <AlertTriangle className="w-5 h-5 text-slate-600" />
                        ) : (
                          <Clock className="w-5 h-5 text-slate-500" />
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-medium">{check.name}</p>
                          <p className="text-xs text-gray-500">{check.note}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs ${check.status === 'pass' ? 'bg-primary/10 text-primary' :
                            check.status === 'violation' ? 'bg-slate-200 text-slate-700' :
                              'bg-slate-100 text-slate-600'
                          }`}>{check.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Checks & Approvals */}
            {bundleCreationStep === 5 && (
              <div className="space-y-6">
                <h3 className="font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> Checks & Approvals
                </h3>

                {/* Automated Checks */}
                <div>
                  <h4 className="font-medium mb-3">Automated Checks</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { name: 'Schema Validation', status: 'passed', output: 'Valid against v1.0' },
                      { name: 'Lint', status: 'passed', output: 'No issues found' },
                      { name: 'Unit Tests', status: 'passed', output: '8/8 passed' },
                      { name: 'Contract Tests', status: 'passed', output: '4/4 passed' },
                      { name: 'Workflow Simulation', status: 'passed', output: 'Dry run successful' },
                      { name: 'Policy Simulation', status: 'passed', output: '1 violation to remediate' }
                    ].map((check, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{check.name}</p>
                          <p className="text-xs text-gray-500">{check.output}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Approval Status */}
                <div>
                  <h4 className="font-medium mb-3">Approval Status</h4>
                  <div className="space-y-2">
                    {newBundleSpec.required_approvals?.map((approver, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <CheckCircle2 className="w-5 h-5 text-primary" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{approver}</p>
                          <p className="text-xs text-gray-500">Approved just now (simulated)</p>
                        </div>
                        <span className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">Approved</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                    <span className="font-medium text-primary">All checks passed - Ready to merge and apply</span>
                  </div>
                </div>
              </div>
            )}

            {/* Step 6: Apply & Verify */}
            {bundleCreationStep === 6 && (
              <div className="space-y-6">
                <h3 className="font-semibold flex items-center gap-2">
                  <Play className="w-4 h-4" /> Merge, Apply & Verify
                </h3>

                {!pipelineSimulation ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-[#4a70ff]/10 flex items-center justify-center mx-auto mb-4">
                      <GitMerge className="w-8 h-8 text-[#4a70ff]" />
                    </div>
                    <h4 className="font-medium mb-2">Ready to Merge & Apply</h4>
                    <p className="text-sm text-gray-500 mb-4">All checks passed, approvals received</p>
                    <button
                      onClick={simulatePipeline}
                      className="px-6 py-3 bg-[#4a70ff] text-white rounded-lg hover:bg-[#3a5fe6] font-medium"
                    >
                      Merge & Apply
                    </button>
                  </div>
                ) : (
                  <div>
                    {/* Pipeline Progress */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <div className="flex items-center gap-2 mb-4">
                        {pipelineSimulation.stages.map((stage, idx) => (
                          <React.Fragment key={stage.name}>
                            <div className="flex flex-col items-center">
                              <div className={`w-8 h-8 rounded-full ${stageStatusColors[stage.status]} flex items-center justify-center`}>
                                {stage.status === 'completed' && <CheckCircle2 className="w-4 h-4 text-white" />}
                                {stage.status === 'running' && <RefreshCw className="w-4 h-4 text-white animate-spin" />}
                                {stage.status === 'pending' && <Clock className="w-4 h-4 text-white" />}
                              </div>
                              <span className="text-[10px] text-gray-500 mt-1 text-center max-w-[60px]">{stage.name}</span>
                            </div>
                            {idx < pipelineSimulation.stages.length - 1 && (
                              <div className={`flex-1 h-0.5 ${stage.status === 'completed' ? 'bg-primary' : 'bg-gray-300'}`} />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                      <div className="space-y-2">
                        {pipelineSimulation.stages.filter(s => s.output).map((stage) => (
                          <div key={stage.name} className="flex items-center gap-3 text-sm">
                            <span className="w-32 text-gray-500 text-xs">{stage.name}</span>
                            <span className={`flex-1 text-xs ${stage.status === 'completed' ? 'text-primary' : 'text-gray-600'}`}>
                              {stage.output}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Evidence Bundle */}
                    {pipelineSimulation.status === 'completed' && (
                      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <Package className="w-5 h-5 text-primary" />
                            <span className="font-medium text-primary">Evidence Bundle Generated</span>
                          </div>
                          <span className="text-sm text-primary">{evidenceBundleTemplate.id}</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {evidenceBundleTemplate.contents.slice(0, 4).map((item, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-primary">
                              <FileText className="w-3.5 h-3.5" />
                              {item.type}
                            </div>
                          ))}
                        </div>
                        <button className="mt-4 px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 flex items-center gap-2">
                          <Download className="w-4 h-4" /> Download Evidence Bundle
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Navigation */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between">
            <button
              onClick={() => {
                if (bundleCreationStep === 1) {
                  setShowProposeBundle(false);
                  setNewBundleSpec(null);
                  setPipelineSimulation(null);
                } else {
                  setBundleCreationStep(prev => prev - 1);
                }
              }}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-100"
            >
              {bundleCreationStep === 1 ? 'Cancel' : 'Back'}
            </button>
            {bundleCreationStep < 6 && (
              <button
                onClick={() => setBundleCreationStep(prev => prev + 1)}
                className="px-4 py-2 bg-[#4a70ff] text-white rounded-lg hover:bg-[#3a5fe6] flex items-center gap-2"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            )}
            {bundleCreationStep === 6 && pipelineSimulation?.status === 'completed' && (
              <button
                onClick={() => {
                  setShowProposeBundle(false);
                  setNewBundleSpec(null);
                  setBundleCreationStep(1);
                  setPipelineSimulation(null);
                  addToast('Changes applied successfully - Asset statuses updated', 'success');
                }}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 w-full sm:w-auto mt-4"
              >
                Complete & Close Wizard
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPRDetail = (pr) => {
    const spec = getChangeSpec(pr.changeSpecId);
    const diffTabs = ['terraform', 'kubernetes', 'policy', 'workflow'].filter(t => pr.diffs[t]);

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 w-full max-w-5xl max-h-[90vh] overflow-hidden my-auto">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-[#4a70ff]" />
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold">{pr.id}</h2>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[pr.status].bg} ${statusColors[pr.status].text}`}>
                    {statusColors[pr.status].label}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{pr.title}</p>
              </div>
            </div>
            <button onClick={() => setSelectedPR(null)} className="text-gray-400 hover:text-gray-600">
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {/* Pipeline Progress - Show for merged PRs */}
            {pr.pipeline && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Play className="w-4 h-4" /> Apply Pipeline
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-4">
                    {pr.pipeline.stages.map((stage, idx) => (
                      <React.Fragment key={stage.name}>
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full ${stageStatusColors[stage.status]} flex items-center justify-center`}>
                            {stage.status === 'completed' && <CheckCircle2 className="w-4 h-4 text-white" />}
                            {stage.status === 'running' && <RefreshCw className="w-4 h-4 text-white animate-spin" />}
                            {stage.status === 'pending' && <Clock className="w-4 h-4 text-white" />}
                            {stage.status === 'failed' && <XCircle className="w-4 h-4 text-white" />}
                          </div>
                          <span className="text-xs text-gray-600 mt-1">{stage.name}</span>
                        </div>
                        {idx < pr.pipeline.stages.length - 1 && (
                          <div className={`flex-1 h-0.5 ${stage.status === 'completed' ? 'bg-primary' : 'bg-gray-300'}`} />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {pr.pipeline.stages.map((stage) => (
                      <div key={stage.name} className="flex items-center gap-3 text-sm">
                        <span className="w-24 text-gray-500">{stage.name}</span>
                        <span className={`flex-1 ${stage.status === 'completed' ? 'text-primary' : 'text-gray-600'}`}>
                          {stage.output}
                        </span>
                        {stage.endTime && (
                          <span className="text-xs text-gray-400">
                            {new Date(stage.endTime).toLocaleTimeString()}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Change Spec JSON */}
            {spec && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Code className="w-4 h-4" /> Change Spec
                </h3>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-sm text-gray-100 font-mono">
                    {JSON.stringify(spec, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Compiled Diffs */}
            {diffTabs.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FileCode className="w-4 h-4" /> Compiled Diffs
                </h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex border-b border-gray-200 bg-gray-50">
                    {diffTabs.map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveDiffTab(tab)}
                        className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${activeDiffTab === tab
                            ? 'bg-white text-[#4a70ff] border-b-2 border-[#4a70ff]'
                            : 'text-gray-600 hover:text-black'
                          }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                  <div className="bg-gray-900 p-4 overflow-x-auto">
                    <pre className="text-sm font-mono">
                      {pr.diffs[activeDiffTab]?.split('\n').map((line, idx) => (
                        <div key={idx} className={`${line.startsWith('+') ? 'text-primary bg-primary/20' :
                            line.startsWith('-') ? 'text-slate-400 bg-slate-900/20' :
                              'text-gray-300'
                          }`}>
                          {line}
                        </div>
                      ))}
                    </pre>
                  </div>
                </div>
              </div>
            )}

            {/* Checks */}
            <div className="mb-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Checks
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(pr.checks).map(([name, check]) => {
                  const StatusIcon = checkStatusIcons[check.status]?.icon || Clock;
                  return (
                    <div key={name} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <StatusIcon className={`w-5 h-5 ${checkStatusIcons[check.status]?.color}`} />
                      <div className="flex-1">
                        <p className="text-sm font-medium capitalize">{name.replace(/([A-Z])/g, ' $1')}</p>
                        <p className="text-xs text-gray-500">{check.output}</p>
                      </div>
                      {check.duration && (
                        <span className="text-xs text-gray-400">{check.duration}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Approvals */}
            <div className="mb-6">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" /> Approvals
              </h3>
              <div className="space-y-2">
                {pr.approvals.required.length > 0 ? (
                  pr.approvals.required.map((role, idx) => {
                    const received = pr.approvals.received.find(r => r.role === role);
                    return (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        {received ? (
                          <CheckCircle2 className="w-5 h-5 text-primary" />
                        ) : (
                          <Clock className="w-5 h-5 text-gray-400" />
                        )}
                        <div className="flex-1">
                          <p className="text-sm font-medium">{role}</p>
                          {received && (
                            <p className="text-xs text-gray-500">
                              Approved by {received.approver} at {new Date(received.time).toLocaleString()}
                            </p>
                          )}
                        </div>
                        {!received && pr.status === 'open' && (
                          <button
                            onClick={() => handleAction(`Request approval from ${role}`)}
                            className="text-xs text-[#4a70ff] hover:underline"
                          >
                            Request
                          </button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="flex items-center gap-3 p-3 bg-primary/10 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                    <p className="text-sm text-primary">Auto-approved (emergency action or low-risk change)</p>
                  </div>
                )}
              </div>
            </div>

            {/* Artifacts */}
            {pr.artifacts && (
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" /> Artifacts
                </h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(pr.artifacts).map(([name, file]) => (
                    <button
                      key={name}
                      onClick={() => handleAction(`Download ${file}`)}
                      className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm text-gray-700 hover:bg-gray-200 transition-colors flex items-center gap-2"
                    >
                      <FileCode className="w-3.5 h-3.5" />
                      {name.replace(/([A-Z])/g, ' $1')}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-2 sticky bottom-0">
            <button onClick={() => setSelectedPR(null)} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-100">
              Close
            </button>
            {pr.status === 'open' && (
              <>
                <button onClick={() => handleAction('Run all checks')} className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-100">
                  Re-run Checks
                </button>
                {pr.approvals.status === 'approved' && (
                  <button onClick={() => handleAction('Execute Bundle')} className="px-4 py-2 bg-[#4a70ff] text-white rounded-lg hover:bg-[#3a5fe6]">
                    Execute Bundle
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="changes-page">
      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-black mb-1">Changes</h1>
            <p className="text-gray-500">Action bundles: Intent in, bundle out, agents execute</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowGuardrails(!showGuardrails)}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <Shield className="w-4 h-4" />
              Guardrails
            </button>
            <Link
              to="/module-catalog"
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <Layers className="w-4 h-4" />
              Module Catalog
            </Link>
          </div>
        </div>

        {showProposeBundle && renderProposeBundleFlow()}
        {selectedPR && renderPRDetail(selectedPR)}

        {/* Guardrails Panel */}
        {showGuardrails && (
          <div className="mb-8 bg-slate-100 border border-slate-300 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-slate-700" />
              <h3 className="font-semibold text-slate-900">GitOps Guardrails</h3>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-medium text-slate-800 mb-2">Production Requirements</h4>
                <ul className="space-y-1">
                  {guardrails.prodRequirements.map((req, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-slate-800 mb-2">Blocked Actions</h4>
                <ul className="space-y-1">
                  {guardrails.blockedActions.map((action, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                      <XCircle className="w-4 h-4 text-slate-600" />
                      {action}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-slate-800 mb-2">Safety Harness</h4>
                <ul className="space-y-1">
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Lock className="w-4 h-4 text-slate-600" />
                    No tests, no apply for prod
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Lock className="w-4 h-4 text-slate-600" />
                    Schema validation required
                  </li>
                  <li className="flex items-center gap-2 text-sm text-slate-700">
                    <Lock className="w-4 h-4 text-slate-600" />
                    High-impact needs approval
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Flow Diagram */}
        <div className="mb-8 bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold mb-4">Agent-Native Flow</h3>
          <div className="flex items-center justify-between overflow-x-auto pb-2">
            {['Intent', 'Change Spec', 'Compile', 'Bundle', 'Checks', 'Approvals', 'Execute', 'Verify', 'Evidence'].map((step, idx) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center min-w-[80px]">
                  <div className="w-10 h-10 rounded-full bg-[#4a70ff] text-white flex items-center justify-center text-sm font-medium">
                    {idx + 1}
                  </div>
                  <span className="text-xs text-gray-600 mt-2 text-center">{step}</span>
                </div>
                {idx < 8 && <ArrowRight className="w-4 h-4 text-gray-300 mx-1 shrink-0" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Open Bundles', value: pullRequests.filter(p => p.status === 'open').length, icon: Package, color: 'primary' },
            { label: 'Merged Today', value: pullRequests.filter(p => p.status === 'merged').length, icon: GitMerge, color: 'slate' },
            { label: 'Pending Approval', value: '1', icon: Clock, color: 'slate-light' },
            { label: 'Checks Passing', value: '98%', icon: CheckCircle2, color: 'primary' }
          ].map((stat, idx) => (
            <div key={idx} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${stat.color === 'primary' ? 'bg-primary/10' :
                    stat.color === 'slate' ? 'bg-slate-200' :
                      stat.color === 'slate-light' ? 'bg-slate-100' : 'bg-gray-100'
                  }`}>
                  <stat.icon className={`w-4.5 h-4.5 ${stat.color === 'primary' ? 'text-primary' :
                      stat.color === 'slate' ? 'text-slate-700' :
                        stat.color === 'slate-light' ? 'text-slate-600' : 'text-gray-600'
                    }`} />
                </div>
              </div>
              <p className="text-2xl font-bold text-black">{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Action Bundles List */}
        <div>
          <h2 className="text-lg font-semibold text-black mb-4">Action Bundles</h2>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="divide-y divide-gray-100">
              {pullRequests.map((pr) => (
                <div
                  key={pr.id}
                  onClick={() => setSelectedPR(pr)}
                  className="px-5 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${pr.status === 'open' ? 'bg-primary/10' :
                        pr.status === 'merged' ? 'bg-slate-200' : 'bg-gray-100'
                      }`}>
                      {pr.status === 'merged' ? (
                        <GitMerge className="w-5 h-5 text-slate-700" />
                      ) : (
                        <Package className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-[#4a70ff]">{pr.id}</span>
                        <span className="font-medium text-black">{pr.title}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColors[pr.status].bg} ${statusColors[pr.status].text}`}>
                          {statusColors[pr.status].label}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">{pr.description}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Change Spec: {pr.changeSpecId}</span>
                        <span>Author: {pr.author}</span>
                        <span>{new Date(pr.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {pr.labels.map((label, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                            {label}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* Checks summary */}
                      <div className="flex items-center gap-1">
                        {Object.values(pr.checks).filter(c => c.status === 'passed').length > 0 && (
                          <span className="flex items-center gap-1 text-xs text-primary">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {Object.values(pr.checks).filter(c => c.status === 'passed').length}
                          </span>
                        )}
                        {Object.values(pr.checks).filter(c => c.status === 'running').length > 0 && (
                          <span className="flex items-center gap-1 text-xs text-primary">
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            {Object.values(pr.checks).filter(c => c.status === 'running').length}
                          </span>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-300" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
